import { Project } from '@avanzu/types';
import { CloudOff, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { apiClient } from '../services';

const ProjectSelectHeader: React.FC = () => {
  const { currentProject, loading, error, setCurrentProject } = useProject();
  const { organisationId } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState<string | null>(null);
  const menuOpen = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchProjects = async () => {
    if (!organisationId) return;

    try {
      setMenuLoading(true);
      setMenuError(null);

      const response = await apiClient.get(`/organisations/${organisationId}/projects`);
      const data = await response.json();

      if (data.success && data.projects) {
        setProjects(data.projects);
      } else {
        setMenuError(data.message || 'Failed to load projects');
      }
    } catch (err) {
      setMenuError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setMenuLoading(false);
    }
  };

  const handleMenuOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    await fetchProjects();
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSelect = (projectId: string) => {
    setCurrentProject(projectId);
    handleMenuClose();
  };

  // Group projects by tax year
  const groupedProjects =
    projects?.reduce(
      (acc, project) => {
        const year = project.taxYear;
        if (!acc[year]) {
          acc[year] = [];
        }
        acc[year].push(project);
        return acc;
      },
      {} as Record<string, Project[]>
    ) || {};

  // Sort years in descending order
  const sortedYears = Object.keys(groupedProjects).sort((a, b) => b.localeCompare(a));

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            sx={{ height: 32 }}
            variant="outlined"
            size="small"
            endIcon={<CircularProgress size={16} />}
            disabled={true}
          >
            Select Project
          </Button>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              Loading project...
            </Typography>
          )}
        </Box>
      );
    }

    // No project selected
    if (!currentProject) {
      return (
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            sx={{ height: 32 }}
            variant="outlined"
            size="small"
            onClick={handleMenuOpen}
            endIcon={<ExpandMoreIcon />}
          >
            Project
          </Button>
          {!isMobile && (
            <Typography variant="body2" color="text.secondary">
              No project selected
            </Typography>
          )}
        </Box>
      );
    }

    // Project selected - show breadcrumb with change button
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button
          sx={{ height: 32 }}
          onClick={handleMenuOpen}
          color="primary"
          variant="outlined"
          size="small"
          endIcon={<ExpandMoreIcon />}
        >
          Project
        </Button>
        {!isMobile && (
          <Breadcrumbs aria-label="project-breadcrumb" separator="›">
            <Typography variant="body2" color="text.primary">
              FY {currentProject.taxYear}
            </Typography>

            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
              {currentProject.name}
            </Typography>
          </Breadcrumbs>
        )}
      </Stack>
    );
  };

  return (
    <Box>
      {renderContent()}

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {menuLoading ? (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading projects...
              </Typography>
            </Box>
          </MenuItem>
        ) : menuError ? (
          <MenuItem
            disabled
            sx={{
              opacity: 1,
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}
          >
            <Box sx={{ px: 1, py: 1 }}>
              <Typography variant="body2" color="error.main">
                <CloudOff fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                {menuError}
              </Typography>
            </Box>
          </MenuItem>
        ) : sortedYears.length > 0 ? (
          sortedYears
            .map((year, yearIndex) => [
              // Year header
              <Box key={`header-${year}`} sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  FY {year}
                </Typography>
              </Box>,

              // Projects for this year
              ...groupedProjects[year].map((project) => (
                <MenuItem
                  key={project.id}
                  onClick={() => handleProjectSelect(project.id)}
                  selected={currentProject?.id === project.id}
                >
                  <ListItemText
                    primary={project.name}
                    secondary={`Budget: ${project.totalBudget?.toLocaleString() || '0'}`}
                  />
                </MenuItem>
              )),

              // Divider between years (except for last year)
              ...(yearIndex < sortedYears.length - 1 ? [<Divider key={`divider-${year}`} />] : []),
            ])
            .flat()
        ) : (
          <MenuItem component={RouterLink} to="/app/projects" onClick={handleMenuClose}>
            <ListItemText primary="No projects found" secondary="Create your first project" />
          </MenuItem>
        )}

        <Divider />
        <MenuItem component={RouterLink} to="/app/projects" onClick={handleMenuClose}>
          <ListItemText primary="Manage Projects" />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProjectSelectHeader;
